import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import useIsDesktop from "@/hooks/useIsDesktop";
import useSlickArrows from "@/hooks/useSlickArrows";
import styles from "./TravelInsights.module.css";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { FiArrowUpRight, FiSearch } from "react-icons/fi";
import { IoEye } from "react-icons/io5";
import { IoIosShareAlt } from "react-icons/io";
import { FaCalendarDays } from "react-icons/fa6";
import Breadcrumbs from "../Breadcrumbs";
import { withRegionPath } from "@/utils/session";
import ShareModal from "@/Component/Module/HolidayDetail/ShareModal";
import { fetchBlogsList, fetchBlogsByCategorySlug } from "@/services/api/blog";
import {
  buildBlogListUrl,
  buildBlogMonthListUrl,
  getRegionPath,
  normalizeBlogsListResponse,
  normalizeCategoryBlogsResponse,
  slugifyCategoryTabId,
} from "@/utils/blogHelpers";
import { scrollToSectionElement } from "@/utils/scrollToSection";
import CityExperiences from "../CityExperiences";

const MIN_BLOG_SEARCH_LENGTH = 3;
const BLOG_SEARCH_DEBOUNCE_MS = 400;
const EMPTY_REGION_SLUGS = Object.freeze([]);

function getBlogShareUrl(slug = "") {
  const path = withRegionPath(`/blog/${slug}`);
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}

const MOCK_INSIGHTS_DATA = [
  {
    id: "1",
    image: "/images/insight1.jpg",
    date: "20 Jan 2026",
    category: "DESTINATION",
    subCategory: "Asia",
    categoryIcon: "/images/icons/travelinsight1.svg",
    title: "15 Romantic Weekend Getaways from the UAE in 2026",
    description:
      "A bunch of flowers is a blessing for couples but what about a bunch of cozy memories..",
    views: "102 Views",
    slug: "romantic-weekend-getaways-uae",
  },
  {
    id: "2",
    image: "/images/insight1.jpg",
    date: "20 Jan 2026",
    category: "ACTIVITIES",
    subCategory: "Africa",
    categoryIcon: "/images/icons/travelinsight2.svg",
    title: "15 Romantic Weekend Getaways from the UAE in 2026",
    description:
      "A bunch of flowers is a blessing for couples but what about a bunch of cozy memories..",
    views: "102 Views",
    slug: "romantic-weekend-getaways-uae-2",
  },
  {
    id: "3",
    image: "/images/insight1.jpg",
    date: "20 Jan 2026",
    category: "TIPS",
    subCategory: "Europe",
    categoryIcon: "/images/icons/travelinsight3.svg",
    title: "15 Romantic Weekend Getaways from the UAE in 2026",
    description:
      "A bunch of flowers is a blessing for couples but what about a bunch of cozy memories..",
    views: "102 Views",
    slug: "romantic-weekend-getaways-uae-3",
  },
  {
    id: "4",
    image: "/images/insight1.jpg",
    date: "20 Jan 2026",
    category: "TRAVEL THEME",
    subCategory: "North & Central America",
    categoryIcon: "/images/icons/travelinsight4.svg",
    title: "15 Romantic Weekend Getaways from the UAE in 2026",
    description:
      "A bunch of flowers is a blessing for couples but what about a bunch of cozy memories..",
    views: "102 Views",
    slug: "romantic-weekend-getaways-uae-4",
  },
];

function InsightCard({ item, onShare, articleHref = "" }) {
  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onShare?.(item);
  };

  const href = articleHref || withRegionPath(`/blog/${item.slug}`);

  return (
    <article className={styles.travelInsightCard}>
      <a href={href} className={styles.travelInsightImageLink} aria-label={item.title}>
        <div className={styles.travelInsightImageWrap}>
          <img
            src={item.image}
            alt={item.title}
            className={styles.travelInsightCardImage}
          />
          {item.date && (
            <span className={styles.travelInsightDateBadge}>
              <FaCalendarDays size={11} color="#d92027" />
              {item.date}
            </span>
          )}
        </div>
      </a>

      <div className={styles.travelInsightBody}>
        {item.category && (
          <div className={styles.travelInsightCategoryTag}>
            {item.categoryIcon && (
              <img
                src={item.categoryIcon}
                alt={item.category}
                className={styles.travelInsightCategoryIcon}
              />
            )}
            <span>{item.category}</span>
          </div>
        )}

        <h3 className={styles.travelInsightCardTitle}>
          <a href={href} className={styles.travelInsightTitleLink}>
            {item.title}
          </a>
        </h3>

        <p className={styles.travelInsightCardDescription}>
          {item.description}
        </p>

        <div className={styles.travelInsightFooter}>
          <div className={styles.travelInsightMetaLeft}>
            <span className={styles.travelInsightViews}>
              <IoEye size={13} />
              {item.views || "0 Views"}
            </span>
            <button
              type="button"
              className={styles.travelInsightShareBtn}
              onClick={handleShare}
              aria-label="Share article"
            >
              <IoIosShareAlt size={18} />
            </button>
          </div>

          <a
            href={href}
            className={styles.travelInsightArrowBtn}
            aria-label="Read full article"
          >
            <FiArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </article>
  );
}

function CategoryRowSection({
  sectionTitle,
  sectionId,
  items,
  isDesktop,
  mounted,
  isScrollableMobilePage,
  onShare,
  getArticleHref,
}) {
  const sliderRef = useRef(null);
  const slidesToShow = 4;
  const { canPrev, canNext, hasOverflow, slickArrowEvents } = useSlickArrows(
    items.length,
    slidesToShow,
    sectionId
  );

  const slickSettings = {
    dots: false,
    infinite: false,
    speed: 400,
    slidesToShow,
    slidesToScroll: 1,
    arrows: false,
    ...slickArrowEvents,
    responsive: [
      { breakpoint: 1101, settings: { slidesToShow: 3 } },
      { breakpoint: 900, settings: { slidesToShow: 2 } },
      { breakpoint: 580, settings: { slidesToShow: 1 } },
    ],
  };

  return (
    <div
      id={sectionId || slugifyCategoryTabId(sectionTitle)}
      className={styles.categorySectionGroup}
    >
      <div className={styles.categoryHeader}>
        <h2 className={styles.categorySectionTitle}>{sectionTitle}</h2>
        {(mounted && isDesktop && hasOverflow) ? (
          <div className="headerArrows">
            <button
              type="button"
              className="headerArrowBtn"
              disabled={!canPrev}
              onClick={() => sliderRef.current?.slickPrev()}
            >
              <MdChevronLeft size={22} />
            </button>
            <button
              type="button"
              className="headerArrowBtn"
              disabled={!canNext}
              onClick={() => sliderRef.current?.slickNext()}
            >
              <MdChevronRight size={22} />
            </button>
          </div>
        ) : null}
      </div>

      {mounted && isDesktop ? (
        <div className={styles.travelInsightSlickWrap}>
          <Slider ref={sliderRef} {...slickSettings}>
            {items.map((item, idx) => (
              <div
                key={`${item.id}-${idx}`}
                className={styles.travelInsightSlickSlide}
              >
                <InsightCard
                  item={item}
                  onShare={onShare}
                  articleHref={getArticleHref?.(item.slug)}
                />
              </div>
            ))}
          </Slider>
        </div>
      ) : (
        <div
          className={`${styles.blogGrid} ${isScrollableMobilePage ? styles.mobileScrollGrid : ""
            }`}
        >
          {items.map((item, idx) => (
            <InsightCard
              key={`${item.id}-${idx}`}
              item={item}
              onShare={onShare}
              articleHref={getArticleHref?.(item.slug)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TravelInsights({
  title = "Travel Insights",
  data,
  className = "",
  isBlogPage = false,
  category = "",
  categoryInfo = null,
  initialCategoryData = null,
  mobileCarousel = false,
  initialBlogList = [],
  initialBlogMeta = null,
  regionTagSlug,
  country = "UAE",
}) {
  const { isDesktop, mounted } = useIsDesktop(900);
  const sliderRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState(
    initialBlogMeta?.searchText || ""
  );
  const [debouncedSearch, setDebouncedSearch] = useState(
    initialBlogMeta?.searchText || ""
  );
  const [clientSearchActive, setClientSearchActive] = useState(false);
  const [visibleCount, setVisibleCount] = useState(8);
  const [activeTab, setActiveTab] = useState("");
  const [shareTarget, setShareTarget] = useState(null);
  const [blogItems, setBlogItems] = useState(initialBlogList || []);
  const [blogTotalCount, setBlogTotalCount] = useState(
    initialBlogMeta?.totalCount || 0
  );
  const [blogPageIndex, setBlogPageIndex] = useState(
    initialBlogMeta?.pageIndex || 1
  );
  const [blogTotalPages, setBlogTotalPages] = useState(
    initialBlogMeta?.totalPages || 1
  );
  const [isBlogLoading, setIsBlogLoading] = useState(false);
  const [isBlogLoadingMore, setIsBlogLoadingMore] = useState(false);
  const [categoryData, setCategoryData] = useState(initialCategoryData);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);

  const isCategoryPage = Boolean(category);
  const regionSlugs = useMemo(() => {
    const source =
      regionTagSlug === undefined || regionTagSlug === null
        ? EMPTY_REGION_SLUGS
        : regionTagSlug;
    if (Array.isArray(source)) {
      const slugs = source
        .map((slug) => String(slug || "").trim())
        .filter(Boolean);
      return slugs.length ? slugs : EMPTY_REGION_SLUGS;
    }
    const single = String(source || "").trim();
    return single ? [single] : EMPTY_REGION_SLUGS;
  }, [regionTagSlug]);

  const isBlogMonthPage = regionSlugs.length > 0;
  const isBlogHomePage = Boolean(isBlogPage) && !category && !isBlogMonthPage;
  const isBlogListPage = isBlogHomePage || isBlogMonthPage;
  const blogListAction = isBlogMonthPage
    ? buildBlogMonthListUrl(country, {
      monthSlug: regionSlugs[0],
      searchText: initialBlogMeta?.searchText || "",
    })
    : buildBlogListUrl(country);
  const getArticleHref = useCallback(
    (slug = "") => getRegionPath(country, `/blog/${slug}`),
    [country]
  );

  useEffect(() => {
    setCategoryData(initialCategoryData);
  }, [initialCategoryData]);

  useEffect(() => {
    if (!isCategoryPage || initialCategoryData) return undefined;

    let cancelled = false;
    setIsCategoryLoading(true);

    fetchBlogsByCategorySlug(category)
      .then((response) => {
        if (cancelled) return;
        setCategoryData(normalizeCategoryBlogsResponse(response, category));
      })
      .catch(() => {
        if (!cancelled) {
          setCategoryData(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsCategoryLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [category, initialCategoryData, isCategoryPage]);

  useEffect(() => {
    if (!isBlogListPage || !clientSearchActive) return undefined;

    const timer = setTimeout(() => {
      const trimmed = searchQuery.trim();
      setDebouncedSearch(
        trimmed.length >= MIN_BLOG_SEARCH_LENGTH ? trimmed : ""
      );
    }, BLOG_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery, isBlogListPage, clientSearchActive]);

  const loadBlogPage = useCallback(
    async ({ pageIndex = 1, searchText = "", append = false } = {}) => {
      if (append) {
        setIsBlogLoadingMore(true);
      } else {
        setIsBlogLoading(true);
      }

      try {
        const response = await fetchBlogsList({
          searchText,
          pageSize: 12,
          pageIndex,
          regionTagSlug: regionSlugs,
        });
        const normalized = normalizeBlogsListResponse(response);

        setBlogItems((prev) =>
          append ? [...prev, ...normalized.items] : normalized.items
        );
        setBlogTotalCount(normalized.totalCount);
        setBlogPageIndex(normalized.pageIndex);
        setBlogTotalPages(normalized.totalPages);

        return normalized;
      } catch (error) {
        if (!append) {
          setBlogItems([]);
          setBlogTotalCount(0);
          setBlogPageIndex(1);
          setBlogTotalPages(1);
        }
        return null;
      } finally {
        if (append) {
          setIsBlogLoadingMore(false);
        } else {
          setIsBlogLoading(false);
        }
      }
    },
    [regionSlugs]
  );

  useEffect(() => {
    if (!isBlogListPage || !clientSearchActive) return;

    loadBlogPage({ pageIndex: 1, searchText: debouncedSearch, append: false });
  }, [isBlogListPage, debouncedSearch, clientSearchActive, loadBlogPage]);

  const handleBlogSearchSubmit = (event) => {
    event.preventDefault();
    const trimmed = searchQuery.trim();
    setClientSearchActive(true);
    setDebouncedSearch(
      trimmed.length >= MIN_BLOG_SEARCH_LENGTH ? trimmed : ""
    );
  };

  const handleBlogSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setClientSearchActive(true);
  };

  const handleShareItem = (item) => {
    if (!item?.slug) return;

    setShareTarget({
      title: item.title || "",
      url: getBlogShareUrl(item.slug),
    });
  };

  const rawList = useMemo(() => {
    if (isBlogListPage) return blogItems;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return [
      ...MOCK_INSIGHTS_DATA,
      ...MOCK_INSIGHTS_DATA,
      ...MOCK_INSIGHTS_DATA,
    ];
  }, [data, isBlogListPage, blogItems]);

  const list = useMemo(() => {
    if (isBlogListPage) return rawList;
    if (!searchQuery.trim()) return rawList;
    return rawList.filter(
      (item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.category.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [rawList, searchQuery, isBlogListPage]);

  const categoryRegions = useMemo(() => {
    if (!isCategoryPage || !categoryData?.regions) return [];

    const query = searchQuery.trim().toLowerCase();

    return categoryData.regions.map((region) => ({
      ...region,
      blogs: query
        ? region.blogs.filter(
          (item) =>
            item.title.toLowerCase().includes(query) ||
            item.description.toLowerCase().includes(query)
        )
        : region.blogs,
    }));
  }, [categoryData, isCategoryPage, searchQuery]);

  const categoryTabs = useMemo(
    () => categoryData?.regions?.map((region) => region.tagName).filter(Boolean) || [],
    [categoryData]
  );

  // Set the first tab as active by default when tabs exist
  useEffect(() => {
    if (categoryTabs && categoryTabs.length > 0) {
      setActiveTab(categoryTabs[0]);
    }
  }, [categoryTabs]);

  const visibleCategoryRegions = useMemo(() => {
    if (!searchQuery.trim()) return categoryRegions;
    return categoryRegions.filter((region) => region.blogs.length > 0);
  }, [categoryRegions, searchQuery]);

  const formattedCategory = (category || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const isThingsToDo = formattedCategory === "thingstodo";
  const isScrollableMobilePage =
    mobileCarousel || !isBlogPage || isThingsToDo || Boolean(category);

  const slidesToShow = 4;
  const { canPrev, canNext, hasOverflow, slickArrowEvents } = useSlickArrows(
    list.length,
    slidesToShow
  );

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 400,
    slidesToShow,
    slidesToScroll: 1,
    arrows: false,
    ...slickArrowEvents,
    responsive: [{ breakpoint: 1101, settings: { slidesToShow: 3 } }],
  };

  const dynamicCategoryTitle =
    categoryInfo?.title ||
    (category
      ? category.charAt(0).toUpperCase() + category.slice(1).replace(/-/g, " ")
      : "");

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    const section = document.getElementById(slugifyCategoryTabId(tabName));
    const element = section?.querySelector("h2") || section;
    if (!element) return;

    scrollToSectionElement(element, {
      fallbackNavHeight: isDesktop ? 76 : 72,
    });
  };

  const handleLoadMore = async () => {
    if (isBlogListPage) {
      if (blogPageIndex < blogTotalPages) {
        await loadBlogPage({
          pageIndex: blogPageIndex + 1,
          searchText: debouncedSearch,
          append: true,
        });
      }
      return;
    }

    setVisibleCount((prev) => prev + 4);
  };

  const showBlogLoadMore =
    isBlogListPage &&
    blogTotalCount > 12 &&
    blogPageIndex < blogTotalPages;

  const loadMoreHref = isBlogMonthPage
    ? buildBlogMonthListUrl(country, {
      monthSlug: regionSlugs[0],
      searchText: debouncedSearch,
      page: blogPageIndex + 1,
    })
    : buildBlogListUrl(country, {
      searchText: debouncedSearch,
      page: blogPageIndex + 1,
    });

  const handleLoadMoreClick = (event) => {
    event.preventDefault();
    handleLoadMore();
  };

  return (
    <>
      <section
        id={isBlogListPage ? "blog-section" : undefined}
        className={`${styles.travelInsightSection} standard-padding ${className}`.trim()}
      >
        {category ? (
          <div className={styles.categoryHeaderContainer}>
            <div className={styles.blogSearchWrapper}>
              <input
                type="text"
                placeholder="Select articles"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.blogSearchInput}
              />
              <FiSearch className={styles.blogSearchIcon} size={20} />
            </div>

            <div className={styles.categoryTabsContainer}>
              {categoryTabs.map((tab, index) => {
                const isActive = activeTab ? activeTab === tab : index === 0;
                return (
                  <button
                    key={tab}
                    type="button"
                    className={`${styles.categoryTabBtn} ${isActive ? styles.categoryTabActive : ""
                      }`}
                    onClick={() => handleTabClick(tab)}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
          </div>
        ) : isBlogPage ? (
          <form
            method="get"
            action={blogListAction}
            className={styles.blogSearchWrapper}
            onSubmit={handleBlogSearchSubmit}
          >
            <input
              type="text"
              name="searchText"
              minLength={MIN_BLOG_SEARCH_LENGTH}
              placeholder="Select articles"
              value={searchQuery}
              onChange={handleBlogSearchChange}
              className={styles.blogSearchInput}
            />
            <button
              type="submit"
              className={styles.blogSearchSubmit}
              aria-label="Search articles"
            >
              <FiSearch size={20} />
            </button>
          </form>
        ) : (
          <div className={styles.travelInsightHeader}>
            <div className={styles.travelInsightHeadingBlock}>
              <h2 className="standard-heading">{title}</h2>
            </div>

            {(hasOverflow && mounted && isDesktop) ? (
              <div className="headerArrows">
                <button
                  type="button"
                  className="headerArrowBtn"
                  disabled={!canPrev}
                  onClick={() => sliderRef.current?.slickPrev()}
                >
                  <MdChevronLeft size={22} />
                </button>
                <button
                  type="button"
                  className="headerArrowBtn"
                  disabled={!canNext}
                  onClick={() => sliderRef.current?.slickNext()}
                >
                  <MdChevronRight size={22} />
                </button>
              </div>
            ) : null}
          </div>
        )}

        {isCategoryLoading ? (
          <div className={styles.loadingWrap}>
            <img
              src="/assets/loading.gif"
              alt="Loading category blogs"
              className={styles.loadingGif}
            />
          </div>
        ) : isCategoryPage ? (
          visibleCategoryRegions.length === 0 ? (
            <p className="nodata">No articles found</p>
          ) : (
            <div className={styles.categorySectionsWrapper}>
              {visibleCategoryRegions.map((region) =>
                region.blogs.length > 0 ? (
                  <CategoryRowSection
                    key={region.id || region.tagName}
                    sectionTitle={region.tagName}
                    sectionId={region.slug || slugifyCategoryTabId(region.tagName)}
                    items={region.blogs}
                    isDesktop={isDesktop}
                    mounted={mounted}
                    isScrollableMobilePage={isScrollableMobilePage}
                    onShare={handleShareItem}
                    getArticleHref={getArticleHref}
                  />
                ) : null
              )}
            </div>
          )
        ) : list.length === 0 && !(isBlogListPage && isBlogLoading) ? (
          <p className="nodata">No insights found</p>
        ) : isBlogPage ? (
          <div className={styles.blogGridWrapper}>
            {isBlogLoading ? (
              <div className={styles.loadingWrap}>
                <img
                  src="/assets/loading.gif"
                  alt="Loading blogs"
                  className={styles.loadingGif}
                />
              </div>
            ) : (
              <>
                <div
                  className={`${styles.blogGrid} ${isScrollableMobilePage ? styles.mobileScrollGrid : ""
                    }`}
                >
                  {list.map((item, index) => (
                    <InsightCard
                      key={`${item.id}-${index}`}
                      item={item}
                      onShare={handleShareItem}
                      articleHref={getArticleHref(item.slug)}
                    />
                  ))}
                </div>
                {(showBlogLoadMore || isBlogLoadingMore) && (
                  <div className={styles.loadMoreWrap}>
                    {isBlogLoadingMore ? (
                      <div className={styles.loadingWrapBottom}>
                        <img
                          src="/assets/loading.gif"
                          alt="Loading more blogs"
                          className={styles.loadingGif}
                        />
                      </div>
                    ) : (
                      <a
                        href={loadMoreHref}
                        className={styles.loadMoreBtn}
                        onClick={handleLoadMoreClick}
                      >
                        Load More Blogs
                      </a>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ) : mounted && isDesktop ? (
          <div className={styles.travelInsightSlickWrap}>
            <Slider ref={sliderRef} {...sliderSettings}>
              {list.map((item) => (
                <div
                  key={item.id || item.slug}
                  className={styles.travelInsightSlickSlide}
                >
                  <InsightCard item={item} onShare={handleShareItem} />
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <div className={`${styles.blogGrid} ${styles.mobileScrollGrid}`}>
            {list.map((item, index) => (
              <InsightCard
                key={`${item.id}-${index}`}
                item={item}
                onShare={handleShareItem}
              />
            ))}
          </div>
        )}
        <ShareModal
          show={Boolean(shareTarget)}
          onHide={() => setShareTarget(null)}
          shareUrl={shareTarget?.url || ""}
          title={shareTarget?.title || ""}
        />
      </section>
      {(isCategoryPage && (category === "things-to-do")) ?
        <CityExperiences data={categoryData.thingsToDo} type={"blog"} />
        :
        null
      }
    </>
  );
}